import axios from '../../../config/axios'
import React, { useEffect, useState } from 'react'
import { Outlet, useOutletContext, useSearchParams } from 'react-router-dom'
import { SectionName, LeftSide, SubHeaderContainer, RightSide, ButtonCreate, OptionsButton, TabsDiv, Tab, RefreshIcon, RefreshIconContainer } from '../../../components/styles/subheader-style'
import refreshAct from '../../../assets/refresh-active.svg'
import { PipelineMain, PipelinePageShell } from './style'
import { Ellipsis, Plus, LayoutGrid, BarChart3, Zap, FileText, ArrowLeft } from 'lucide-react'
import PipelineOptionsDropdown from './PipelineOptionsDropdown'
import { usePipelineContext } from './PipelineContext'
import { PipelineProvider } from './PipelineContext'
import AutomationsPage from './Automations'
import CreateAutomation from './Automations/CreateAutomation'
import { ViewModeSelector, SortBySelector, FilterDropdown, ControlsContainer, PipelineAppliedFilters } from './PipelineControls'
import styled from 'styled-components'
import { TypeMainOutlet } from '..'
import DeletedRecordsModal from '../../../components/StandardPage/DeletedRecordsModal'

const BackButton = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.2rem;
  color: #0090D3;
  cursor: pointer;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 2rem;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  transition: all 0.2s ease;
  gap: 0.5rem;

  &:hover {
    background-color: #E6F4FB;
  }
`

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const PipelineSubHeader: React.FC<{ pipelineName: string, pipelineId: number }> = ({ pipelineName, pipelineId }) => {
  const [viewDropdownOptions, setViewDropdownOptions] = useState<boolean>(false)
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState<boolean>(false)
  const {
    setIsConfiguringPipeline,
    setViewOrEditTagsModalIsOpen,
    setAutomationsPageIsOpen,
    isConfiguringPipeline,
    automationsPageIsOpen,
    activeAutomationTab,
    setActiveAutomationTab,
    isCreatingAutomation,
    setIsCreatingAutomation,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    columnSort,
    setColumnSort,
    setIsAddingTableCard,
    showArchived,
    requestReload,
    isReloading
  } = usePipelineContext()

  if (isCreatingAutomation) {
    return null
  }

  if (automationsPageIsOpen) {
    return (
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>Automação</SectionName>
          <TabsDiv>
            <Tab
              $isFirst
              $isActive={activeAutomationTab === 'automations'}
              onClick={() => setActiveAutomationTab('automations')}
            >
              <LayoutGrid size={16} style={{ marginRight: '0.4rem' }} /> Automações
            </Tab>
            <Tab
              $isActive={activeAutomationTab === 'connections'}
              onClick={() => setActiveAutomationTab('connections')}
            >
              <BarChart3 size={16} style={{ marginRight: '0.4rem' }} /> Conexões
            </Tab>
            <Tab
              $isActive={activeAutomationTab === 'integration'}
              onClick={() => setActiveAutomationTab('integration')}
            >
              <Zap size={16} style={{ marginRight: '0.4rem' }} /> Integração
            </Tab>
            <Tab
              $isLast
              $isActive={activeAutomationTab === 'documentation'}
              onClick={() => setActiveAutomationTab('documentation')}
            >
              <FileText size={16} style={{ marginRight: '0.4rem' }} /> Documentação
            </Tab>
          </TabsDiv>
        </LeftSide>
        <RightSide>
          <BackButton onClick={() => setAutomationsPageIsOpen(false)}>
            <ArrowLeft size={16} /> Voltar
          </BackButton>
          <ButtonCreate onClick={() => setIsCreatingAutomation(true)}>
            <Plus size={16} style={{ marginRight: '0.4rem' }} /> Nova automação
          </ButtonCreate>
        </RightSide>
      </SubHeaderContainer>
    )
  }

  return (
    <SubHeaderContainer>
      <LeftSide>
        <SectionName>
          {pipelineName}{showArchived && <span style={{ color: '#dc2626', marginLeft: '0.4rem' }}>(Arquivados)</span>}
        </SectionName>
        <ViewModeSelector value={viewMode} onChange={setViewMode} />
      </LeftSide>
      <RightSide>
        <HeaderControls>
          <SortBySelector
            value={columnSort ? '' : sortBy}
            onChange={(v) => {
              setColumnSort(null)
              setSortBy(v as any)
            }}
          />
          <FilterDropdown />
        </HeaderControls>
        {viewMode === 'table' && (
          <ButtonCreate onClick={() => setIsAddingTableCard(true)}>
            <Plus size={16} style={{ marginRight: '0.4rem' }} /> Adicionar card
          </ButtonCreate>
        )}
        {isConfiguringPipeline &&
          <ButtonCreate onClick={() => setIsConfiguringPipeline(false)}>Concluído</ButtonCreate>
        }
        <RefreshIconContainer onClick={requestReload} title="Atualizar">
          <RefreshIcon src={refreshAct} alt="Atualizar" $spinning={isReloading} />
        </RefreshIconContainer>
        <OptionsButton onClick={() => setViewDropdownOptions(!viewDropdownOptions)}>
          <Ellipsis size={18} />
        </OptionsButton>
        {viewDropdownOptions && (
          <PipelineOptionsDropdown
            onClose={() => setViewDropdownOptions(false)}
            onConfigurePipeline={() => setIsConfiguringPipeline(true)}
            onViewTags={() => setViewOrEditTagsModalIsOpen(true)}
            onViewAutomations={() => setAutomationsPageIsOpen(true)}
            onViewDeletedRecords={() => setIsDeletedModalOpen(true)}
            pipelineId={pipelineId}
            pipelineName={pipelineName}
          />
        )}
      </RightSide>
      {isDeletedModalOpen && (
        <DeletedRecordsModal
          tableName="Ticket"
          moduleName={pipelineName || 'Tickets / Pipelines'}
          onClose={() => setIsDeletedModalOpen(false)}
          onRecordRestored={() => requestReload()}
        />
      )}
    </SubHeaderContainer>
  )
}

const PipelineContent = () => {
  const mainOutletContext = useOutletContext<TypeMainOutlet>()
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const [pipeline, setPipeline] = useState<{ id: number, name: string }>({ id: 0, name: '' })

  useEffect(() => {
    const fetchPipeline = async () => {
      if (!pipelineId || pipelineId === 'null') {
        console.warn('[PipelineContent] pipelineId inválido:', pipelineId)
        return
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}`)
        if (response.data) setPipeline(response.data.data)
      } catch (error) {
        console.error('[PipelineContent] erro fetchPipeline', error)
      }
    }
    fetchPipeline()
  }, [pipelineId])

  return (
    <PipelineProvider>
      <PipelinePageShell>
        <PipelineSubHeader pipelineName={pipeline.name || ''} pipelineId={pipeline.id} />
        <PipelineAppliedFilters />
        <PipelineContentInner pipelineName={pipeline.name || ''} mainOutletContext={mainOutletContext} />
      </PipelinePageShell>
    </PipelineProvider>
  )
};

const PipelineContentInner: React.FC<{ pipelineName: string, mainOutletContext: TypeMainOutlet }> = ({ pipelineName, mainOutletContext }) => {
  const { automationsPageIsOpen, setPipelineName, isCreatingAutomation } = usePipelineContext()

  useEffect(() => {
    if (pipelineName) {
      setPipelineName(pipelineName)
    }
  }, [pipelineName, setPipelineName])

  if (isCreatingAutomation) {
    return (
      <PipelineMain>
        <CreateAutomation />
      </PipelineMain>
    )
  }

  if (automationsPageIsOpen) {
    return (
      <PipelineMain>
        <AutomationsPage />
      </PipelineMain>
    )
  }

  return (
    <PipelineMain>
      <Outlet context={mainOutletContext} />
    </PipelineMain>
  )
}

export default PipelineContent;